import React from "react";
import * as QueryBuilder from "@syncfusion/ej2-react-querybuilder";
import { Internationalization } from "@syncfusion/ej2-base";
import SchemaActions from "../actions/SchemaActions";
import { ButtonComponent } from "@syncfusion/ej2-react-buttons";
import { SplitButtonComponent, MenuEventArgs, ItemModel } from "@syncfusion/ej2-react-splitbuttons";
import { buildHierarchyConsolidationMap, collapseRedundantRuleGroups, convertApiRulesToQueryBuilderRules, normalizeExternalDateValueForApi, resolveConsolidatedRules } from "../helpers/AssetQueryRules";
import FacilityStore from "../stores/FacilityStore";
import dataSourcesApi from "../dataSources/api/dataSourcesApi";
import type { DataSourceModel } from "../dataSources/types";
import {
  EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE,
  EXTERNAL_ATTRIBUTE_DRIFT_STATUS,
} from "./configuration/externalAttributes/types";

declare var InovxLoader: any;

const { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } = React;
const { QueryBuilderComponent } = QueryBuilder;
type ColumnsModel = QueryBuilder.ColumnsModel;
type RuleModel = QueryBuilder.RuleModel;
type QueryBuilderComponentType = QueryBuilder.QueryBuilderComponent;
type QueryRuleNode = {
  field?: string;
  value?: unknown;
  type?: ColumnsModel["type"];
  rules?: QueryRuleNode[];
};
type AssetClassWithExternalAttributes = IAssetClassDetail & {
  ExternalDataSourceLinks?: IExternalDataSourceLink[];
  ExternalAttributeDefinitions?: IExternalAttributeDefinition[];
};
type AssetClassExternalSchema = Pick<IAssetClassUpdateDetail, "ID" | "PrimaryAssetClassID" | "ExternalDataSourceLinks" | "ExternalAttributeDefinitions">;

const dataSource: Record<string, unknown>[] = [];
const fallbackColumns: ColumnsModel[] = [];

const translateQueryBuilder = (key: string) => InovxLoader.t(`facility:queryBuilder.${key}`);

const queryBuilderOperatorKeys: Record<string, string> = {
  startswith: "startsWith",
  notstartswith: "notStartsWith",
  endswith: "endsWith",
  notendswith: "notEndsWith",
  contains: "contains",
  notcontains: "notContains",
  equal: "equal",
  notequal: "notEqual",
  isnotnull: "isSet",
  isnull: "isNotSet",
  greaterthan: "greaterThan",
  greaterthanorequal: "greaterThanOrEqual",
  lessthan: "lessThan",
  lessthanorequal: "lessThanOrEqual",
  between: "between",
  notbetween: "notBetween",
  in: "in",
  notin: "notIn",
};

const getQueryBuilderOperators = (values: string[]): ColumnsModel["operators"] => values.map((value) => ({
  value,
  key: translateQueryBuilder(queryBuilderOperatorKeys[value]),
}));

const getDefaultOperatorsByType = (): Record<NonNullable<ColumnsModel["type"]>, ColumnsModel["operators"]> => ({
  string: getQueryBuilderOperators(["startswith", "notstartswith", "endswith", "notendswith", "contains", "notcontains", "equal", "notequal", "isnotnull", "isnull"]),
  date: getQueryBuilderOperators(["equal", "notequal", "greaterthan", "greaterthanorequal", "lessthan", "lessthanorequal", "between", "notbetween", "isnotnull", "isnull"]),
  boolean: getQueryBuilderOperators(["equal", "isnotnull", "isnull"]),
  number: getQueryBuilderOperators(["equal", "notequal", "greaterthan", "greaterthanorequal", "lessthan", "lessthanorequal", "between", "notbetween", "isnotnull", "isnull"]),
});

const getDeletedLabel = () => `(${translateQueryBuilder("deleted")})`;
const getDeletedIndexedLabel = (index: number) => `(${translateQueryBuilder("deleted")} ${index})`;
const getExternalAttributeCategoryFallback = () => translateQueryBuilder("externalAttributes");
const externalAttributeApiPrefix = "assetidentity.externalattributes.";
const fdwSyncStatusSucceeded = 1;

const parseLocalizedQueryBuilderDate = (value: string): Date | null => {
  const locale = InovxLoader.getSyncfusionJS2Locale() || undefined;
  return new Internationalization(locale).parseDate(value, { type: "date", skeleton: "yMd" });
};

// Track which attribute paths are hierarchy attributes (format: "assetClassId/consolidatedField")
// consolidatedField is pipe-separated sorted IDs for multi-member groups.
let hierarchyAttributePaths: Set<string> = new Set();

const dataTypeMap: Record<number, ColumnsModel["type"]> = {
  1: "string",
  2: "date",
  3: "string",
  4: "boolean",
  5: "number",
};

const getExternalAttributeClassPath = (assetClassId: string, definitionId: string): string =>
  `${assetClassId}/${definitionId}`;

const normalizeId = (id: string | null | undefined): string => (id ?? "").toLowerCase();

const hasUsableExternalMatchDefinition = (
  definitions: IExternalAttributeDefinition[] | undefined,
  linkId: string
): boolean => {
  const normalizedLinkId = normalizeId(linkId);
  return (definitions ?? []).some((definition) => {
    if (!definition?.IsForeignKey ||
        normalizeId(definition.ExternalDataSourceLinkID) !== normalizedLinkId ||
        !definition.SourceColumnName) {
      return false;
    }

    if (definition.AssetMatchTargetType === EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE.AssetName) {
      return true;
    }

    return definition.AssetMatchTargetType === EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE.AssetAttribute &&
      !!definition.AssetMatchAttributeDefinitionID;
  });
};

const isExternalDefinitionQueryable = (
  assetClass: AssetClassWithExternalAttributes,
  definition: IExternalAttributeDefinition,
  linkById: Map<string, IExternalDataSourceLink>,
  dataSourceById: Map<string, DataSourceModel>
): boolean => {
  if (!definition?.ID || definition.DriftStatus !== EXTERNAL_ATTRIBUTE_DRIFT_STATUS.None) {
    return false;
  }

  const link = linkById.get(normalizeId(definition.ExternalDataSourceLinkID));
  if (!link) {
    return false;
  }

  const dataSource = dataSourceById.get(normalizeId(link.DataSourceID));
  if (!dataSource || dataSource.FdwStatus !== fdwSyncStatusSucceeded || !dataSource.FdwTableName) {
    return false;
  }

  return hasUsableExternalMatchDefinition(assetClass.ExternalAttributeDefinitions, link.ID);
};

type InitialRulesResolution = {
  resolvedRules: QueryRuleNode | undefined;
  deletedAssetClassLabels: string[];
};

type SchemaInfo = {
  validClassAttributePaths: Set<string>;
  knownAttributeIds: Set<string>;
  knownExternalAttributeIds: Set<string>;
  validExternalAttributeIds: Set<string>;
};

type AssetClassMaps = {
  nameToId: Map<string, string>;
  idToName: Map<string, string>;
};

const normalizeType = (type: ColumnsModel["type"] | undefined): NonNullable<ColumnsModel["type"]> => type ?? "string";

const hasExternalAttributeDefinition = (
  assetClasses: IAssetClassDetail[],
  assetClassId: string,
  definitionId: string
): boolean => {
  const assetClass = assetClasses.find((item) => item?.ID === assetClassId) as AssetClassWithExternalAttributes | undefined;
  return (assetClass?.ExternalAttributeDefinitions ?? []).some((definition) => definition?.ID === definitionId);
};

const getExternalAttributeDefinition = (
  assetClasses: IAssetClassDetail[],
  assetClassId: string,
  definitionId: string
): IExternalAttributeDefinition | undefined => {
  const assetClass = assetClasses.find((item) => item?.ID === assetClassId) as AssetClassWithExternalAttributes | undefined;
  return (assetClass?.ExternalAttributeDefinitions ?? []).find((definition) => definition?.ID === definitionId);
};

const mergeById = <T extends { ID: string }>(preferred: T[], fallback: T[]): T[] => {
  const preferredIds = new Set(preferred.map((item) => normalizeId(item?.ID)));
  return [...preferred, ...fallback.filter((item) => !preferredIds.has(normalizeId(item?.ID)))];
};

// The facility detail already carries external links/definitions merged from the
// primary class and its asset class extensions. The schema update data in
// FacilityStore is fresher but covers a single schema, so it never contains
// extension-class definitions when the primary schema is loaded. Merge the two:
// store entries (own class plus any extension classes present, matched through
// PrimaryAssetClassID) win by ID, facility-detail entries fill the rest.
const mergeExternalSchema = (
  assetClasses: IAssetClassDetail[],
  externalSchemaClasses: AssetClassExternalSchema[]
): IAssetClassDetail[] => {
  if (!externalSchemaClasses.length) {
    return assetClasses;
  }

  const externalSchemaByClassId = new Map(
    externalSchemaClasses
      .filter((assetClass) => assetClass?.ID)
      .map((assetClass) => [assetClass.ID, assetClass])
  );

  const extensionSchemasByPrimaryClassId = new Map<string, AssetClassExternalSchema[]>();
  externalSchemaClasses.forEach((assetClass) => {
    if (!assetClass?.ID || !assetClass.PrimaryAssetClassID) {
      return;
    }
    const extensions = extensionSchemasByPrimaryClassId.get(assetClass.PrimaryAssetClassID) ?? [];
    extensions.push(assetClass);
    extensionSchemasByPrimaryClassId.set(assetClass.PrimaryAssetClassID, extensions);
  });

  return assetClasses.map((assetClass) => {
    const ownSchema = assetClass?.ID ? externalSchemaByClassId.get(assetClass.ID) : undefined;
    const extensionSchemas = (assetClass?.ID ? extensionSchemasByPrimaryClassId.get(assetClass.ID) : undefined) ?? [];
    if (!ownSchema && !extensionSchemas.length) {
      return assetClass;
    }

    const currentExternal = assetClass as AssetClassWithExternalAttributes;
    const refreshedLinks = [
      ...(ownSchema?.ExternalDataSourceLinks ?? []),
      ...extensionSchemas.flatMap((schema) => schema.ExternalDataSourceLinks ?? []),
    ];
    const refreshedDefinitions = [
      ...(ownSchema?.ExternalAttributeDefinitions ?? []),
      ...extensionSchemas.flatMap((schema) => schema.ExternalAttributeDefinitions ?? []),
    ];

    return {
      ...assetClass,
      ExternalDataSourceLinks: mergeById(refreshedLinks, currentExternal.ExternalDataSourceLinks ?? []),
      ExternalAttributeDefinitions: mergeById(refreshedDefinitions, currentExternal.ExternalAttributeDefinitions ?? []),
    } as AssetClassWithExternalAttributes;
  });
};

const buildAssetClassMaps = (assetClasses: IAssetClassDetail[]): AssetClassMaps => {
  const nameToId = new Map<string, string>();
  const idToName = new Map<string, string>();

  assetClasses.forEach((assetClass) => {
    const id = assetClass?.ID;
    const name = assetClass?.Name;
    if (!id || !name) {
      return;
    }

    if (!nameToId.has(name)) {
      nameToId.set(name, id);
    }

    if (!idToName.has(id)) {
      idToName.set(id, name);
    }
  });

  return { nameToId, idToName };
};

const mapAssetClassValueToIds = (value: unknown, maps: AssetClassMaps): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item !== "string") {
        return item;
      }
      return maps.nameToId.get(item) ?? item;
    });
  }

  if (typeof value === "string") {
    return maps.nameToId.get(value) ?? value;
  }

  return value;
};

const mapAssetClassValueToNames = (value: unknown, maps: AssetClassMaps): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item !== "string") {
        return item;
      }
      return maps.idToName.get(item) ?? item;
    });
  }

  if (typeof value === "string") {
    return maps.idToName.get(value) ?? value;
  }

  return value;
};

const getSchemaInfo = (assetClasses: IAssetClassDetail[], dataSources: DataSourceModel[] = []): SchemaInfo => {
  const validClassAttributePaths = new Set<string>();
  const knownAttributeIds = new Set<string>();
  const knownExternalAttributeIds = new Set<string>();
  const validExternalAttributeIds = new Set<string>();
  const dataSourceById = new Map(dataSources.map((source) => [normalizeId(source.ID), source]));

  const consolidation = buildHierarchyConsolidationMap(assetClasses);

  assetClasses.forEach((assetClass) => {
    const assetClassWithExternal = assetClass as AssetClassWithExternalAttributes;
    const linkById = new Map((assetClassWithExternal.ExternalDataSourceLinks ?? [])
      .filter((link) => link?.ID)
      .map((link) => [normalizeId(link.ID), link]));

    (assetClass.AttributeDefinitions ?? []).forEach((attribute) => {
      if (!attribute?.ID || !assetClass?.ID) return;
      knownAttributeIds.add(attribute.ID);
      validClassAttributePaths.add(`${assetClass.ID}/${attribute.ID}`);
    });

    (assetClass.HierarchyAttributeDefinitions ?? []).forEach((attribute) => {
      if (!attribute?.ID || !assetClass?.ID) return;
      knownAttributeIds.add(attribute.ID);
    });

    (assetClassWithExternal.ExternalAttributeDefinitions ?? []).forEach((definition) => {
      if (!definition?.ID || !assetClass?.ID) return;
      knownExternalAttributeIds.add(definition.ID);
      if (isExternalDefinitionQueryable(assetClassWithExternal, definition, linkById, dataSourceById)) {
        validClassAttributePaths.add(getExternalAttributeClassPath(assetClass.ID, definition.ID));
        validExternalAttributeIds.add(definition.ID);
      }
    });
  });

  consolidation.consolidatedGroups.forEach((_group, key) => {
    validClassAttributePaths.add(key);
  });

  return { validClassAttributePaths, knownAttributeIds, knownExternalAttributeIds, validExternalAttributeIds };
};

const walkRuleFields = (rules: QueryRuleNode | null | undefined, onRule: (rule: QueryRuleNode) => void) => {
  if (!rules) {
    return;
  }

  const walk = (rule: QueryRuleNode) => {
    if (!rule) {
      return;
    }

    if (Array.isArray(rule.rules)) {
      rule.rules.forEach(walk);
      return;
    }

    if (typeof rule.field === "string") {
      onRule(rule);
    }
  };

  walk(rules);
};

const hasLeafRules = (rules: QueryRuleNode | null | undefined): boolean => {
  if (!rules) {
    return false;
  }

  if (Array.isArray(rules.rules)) {
    return rules.rules.some((rule) => hasLeafRules(rule));
  }

  return typeof rules.field === "string" && rules.field.length > 0;
};

const getRenderableRootRules = (rules: RuleModel | null | undefined): RuleModel[] => {
  const rootRules = Array.isArray(rules?.rules) ? (rules.rules as RuleModel[]) : [];
  return rootRules.filter((rule) => hasLeafRules(rule as QueryRuleNode));
};

const clearRenderedRules = (queryBuilder: QueryBuilderComponentType) => {
  const ruleIds = Array.from(queryBuilder.element.querySelectorAll(".e-rule-container"))
    .map((ruleElement) => ruleElement.id.replace(`${queryBuilder.element.id}_`, ""))
    .filter(Boolean);

  if (ruleIds.length > 0) {
    queryBuilder.deleteRules(ruleIds);
  }
};

const normalizeAssetClassRulesForUi = (
  rules: QueryRuleNode | undefined,
  assetClasses: IAssetClassDetail[]
): QueryRuleNode | undefined => {
  if (!rules) {
    return undefined;
  }

  const assetClassMaps = buildAssetClassMaps(assetClasses);

  const normalizeRule = (rule: QueryRuleNode): QueryRuleNode => {
    if (!rule) {
      return rule;
    }

    if (Array.isArray(rule.rules)) {
      return {
        ...rule,
        rules: rule.rules.map(normalizeRule),
      };
    }

    if (rule.field === "assetidentity.assetclass.id") {
      return {
        ...rule,
        field: "assetidentity.assetclass.name",
        value: mapAssetClassValueToNames(rule.value, assetClassMaps),
      };
    }

    if (rule.field === "assetidentity.assetclass.name") {
      return {
        ...rule,
        value: mapAssetClassValueToNames(rule.value, assetClassMaps),
      };
    }

    return rule;
  };

  return normalizeRule(rules);
};

const resolveDeletedAssetClassValues = (
  rules: QueryRuleNode | undefined,
  assetClasses: IAssetClassDetail[]
): InitialRulesResolution => {
  if (!rules) {
    return { resolvedRules: undefined, deletedAssetClassLabels: [] };
  }

  const knownNames = new Set(assetClasses.map((assetClass) => assetClass.Name).filter(Boolean));
  const missingNames = new Set<string>();

  walkRuleFields(rules, (rule) => {
    if (rule.field !== "assetidentity.assetclass.name") {
      return;
    }

    if (Array.isArray(rule.value)) {
      rule.value.forEach((value) => {
        if (typeof value === "string" && value && !knownNames.has(value)) {
          missingNames.add(value);
        }
      });
      return;
    }

    if (typeof rule.value === "string" && rule.value && !knownNames.has(rule.value)) {
      missingNames.add(rule.value);
    }
  });

  if (missingNames.size === 0) {
    return { resolvedRules: rules, deletedAssetClassLabels: [] };
  }

  const missingNamesOrdered = Array.from(missingNames);
  const missingNameToLabel = new Map<string, string>();
  const multipleMissing = missingNamesOrdered.length > 1;
  missingNamesOrdered.forEach((name, index) => {
    missingNameToLabel.set(name, multipleMissing ? getDeletedIndexedLabel(index + 1) : getDeletedLabel());
  });

  const mapRule = (rule: QueryRuleNode): QueryRuleNode => {
    if (Array.isArray(rule.rules)) {
      return {
        ...rule,
        rules: rule.rules.map(mapRule),
      };
    }

    if (rule.field !== "assetidentity.assetclass.name") {
      return rule;
    }

    if (Array.isArray(rule.value)) {
      return {
        ...rule,
        value: rule.value.map((value) => {
          if (typeof value !== "string") {
            return value;
          }
          return missingNameToLabel.get(value) ?? value;
        }),
      };
    }

    if (typeof rule.value === "string") {
      return {
        ...rule,
        value: missingNameToLabel.get(rule.value) ?? rule.value,
      };
    }

    return rule;
  };

  return {
    resolvedRules: mapRule(rules),
    deletedAssetClassLabels: Array.from(missingNameToLabel.values()),
  };
};

const sortColumnsByCategoryAndLabelAsc = (columns: ColumnsModel[]): ColumnsModel[] => {
  return [...columns].sort((a, b) => {
    const categoryA = String(a.category ?? "");
    const categoryB = String(b.category ?? "");
    const categoryCompare = categoryA.localeCompare(categoryB);
    if (categoryCompare !== 0) {
      return categoryCompare;
    }

    const labelA = String(a.label ?? "");
    const labelB = String(b.label ?? "");
    const labelCompare = labelA.localeCompare(labelB);
    if (labelCompare !== 0) {
      return labelCompare;
    }

    return String(a.field ?? "").localeCompare(String(b.field ?? ""));
  });
};

function buildColumns(
  assetClasses: IAssetClassDetail[],
  initialRules?: unknown,
  deletedAssetClassLabels: string[] = [],
  dataSources: DataSourceModel[] = []
): ColumnsModel[] {
  const defaultOperatorsByType = getDefaultOperatorsByType();
  const dataSourceById = new Map(dataSources.map((source) => [normalizeId(source.ID), source]));

  // Reset hierarchy tracking
  hierarchyAttributePaths = new Set();
  const consolidation = buildHierarchyConsolidationMap(assetClasses);
  
  const assetClassNames = Array.from(new Set([
    ...assetClasses.map((assetClass) => assetClass.Name).filter(Boolean),
    ...deletedAssetClassLabels,
  ]));
  const assetClassWithAttributes = assetClasses.filter(
    (assetClass) => {
      const assetClassWithExternal = assetClass as AssetClassWithExternalAttributes;
      return (assetClass.AttributeDefinitions?.length ?? 0) > 0 ||
        (assetClass.HierarchyAttributeDefinitions?.length ?? 0) > 0 ||
        (assetClassWithExternal.ExternalAttributeDefinitions?.length ?? 0) > 0;
    }
  );

  const classColumns: ColumnsModel[] = assetClassWithAttributes.map((assetClass) => {
      const assetClassWithExternal = assetClass as AssetClassWithExternalAttributes;
      const linkById = new Map((assetClassWithExternal.ExternalDataSourceLinks ?? [])
        .filter((link) => link?.ID)
        .map((link) => [normalizeId(link.ID), link]));

      // Nested column fields are pre-prefixed with the asset class ID because the
      // Syncfusion Query Builder rewrites bare child fields to "{parent}/{child}"
      // itself, but defers that work across animation frames once a column list
      // exceeds 50 entries. Rules restored via setRules() before that deferred pass
      // completes then fail to match any sub-field and crash the control. Emitting
      // the final field shape (plus type "object" on group columns) makes the
      // library's mutation pass a no-op, so restore no longer races it.
      const regularColumns = assetClass.AttributeDefinitions?.map((attribute) => ({
        field: `${assetClass.ID}/${attribute.ID}`,
        label: attribute.Name,
        type: dataTypeMap[attribute.DataType] ?? "string",
        operators: defaultOperatorsByType[dataTypeMap[attribute.DataType] ?? "string"],
        category: attribute.SetName,
      })) ?? [];

      const externalColumns = (assetClassWithExternal.ExternalAttributeDefinitions ?? [])
        .filter((definition) => isExternalDefinitionQueryable(assetClassWithExternal, definition, linkById, dataSourceById))
        .map((definition) => {
          const type = dataTypeMap[definition.DataType] ?? "string";
          const link = linkById.get(normalizeId(definition.ExternalDataSourceLinkID));
          return {
            field: `${assetClass.ID}/${definition.ID}`,
            label: definition.Name,
            type,
            operators: defaultOperatorsByType[type],
            category: link?.DisplayName || getExternalAttributeCategoryFallback(),
          };
        });

      const hierarchyColumns: ColumnsModel[] = [];
      const seenConsolidatedFields = new Set<string>();

      (assetClass.HierarchyAttributeDefinitions ?? []).forEach((attribute) => {
        if (!attribute?.ID || !assetClass?.ID) return;
        const consolidatedField = consolidation.individualToConsolidated.get(`${assetClass.ID}/${attribute.ID}`) ?? attribute.ID;
        if (seenConsolidatedFields.has(consolidatedField)) return;
        seenConsolidatedFields.add(consolidatedField);

        hierarchyAttributePaths.add(`${assetClass.ID}/${consolidatedField}`);

        const groupMeta = consolidation.consolidatedGroups.get(`${assetClass.ID}/${consolidatedField}`);
        hierarchyColumns.push({
          field: `${assetClass.ID}/${consolidatedField}`,
          label: groupMeta?.name ?? attribute.Name,
          type: dataTypeMap[groupMeta?.dataType ?? attribute.DataType] ?? "string",
          operators: defaultOperatorsByType[dataTypeMap[groupMeta?.dataType ?? attribute.DataType] ?? "string"],
          category: groupMeta?.setName ?? attribute.SetName,
        });
      });

      return {
        field: assetClass.ID,
        label: assetClass.Name,
        type: "object" as const,
        columns: sortColumnsByCategoryAndLabelAsc([
          ...regularColumns,
          ...hierarchyColumns,
          ...externalColumns,
        ]),
        category: translateQueryBuilder("schemaAttributes"),
      };
    });

  const schemaInfo = getSchemaInfo(assetClasses, dataSources);
  const classColumnsById = new Map<string, ColumnsModel>();
  classColumns.forEach((column) => {
    if (typeof column.field === "string") {
      classColumnsById.set(column.field, column);
    }
  });

  const deletedStandaloneColumnsByField = new Map<string, ColumnsModel>();

  walkRuleFields(initialRules as QueryRuleNode | null | undefined, (rule) => {
    const field = rule.field as string;
    const type = normalizeType(rule.type);

    const slashFieldParts = field.split("/");
    if (slashFieldParts.length === 2) {
      const assetClassId = slashFieldParts[0];
      const attributePart = slashFieldParts[1];
      if (!schemaInfo.validClassAttributePaths.has(field)) {
        const isKnownExternalAttribute = schemaInfo.knownExternalAttributeIds.has(attributePart);
        if (isKnownExternalAttribute) {
          let classColumn = classColumnsById.get(assetClassId);
          if (!classColumn) {
            classColumn = {
              field: assetClassId,
              label: getDeletedLabel(),
              type: "object",
              columns: [],
              category: translateQueryBuilder("schemaAttributes"),
            };
            classColumnsById.set(assetClassId, classColumn);
            classColumns.push(classColumn);
          }

          const nestedColumns = ((classColumn.columns as ColumnsModel[]) ?? []);
          classColumn.columns = nestedColumns;
          if (!nestedColumns.some((column) => column.field === field)) {
            nestedColumns.push({
              field,
              label: getDeletedLabel(),
              type,
              operators: defaultOperatorsByType[type],
              category: getExternalAttributeCategoryFallback(),
            });
          }
          return;
        }

        const individualIds = attributePart.split("|");
        const allKnown = individualIds.length > 1 && individualIds.every((id) => schemaInfo.knownAttributeIds.has(id));
        if (!allKnown) {
          let classColumn = classColumnsById.get(assetClassId);
          if (!classColumn) {
            classColumn = {
              field: assetClassId,
              label: getDeletedLabel(),
              type: "object",
              columns: [],
              category: translateQueryBuilder("schemaAttributes"),
            };
            classColumnsById.set(assetClassId, classColumn);
            classColumns.push(classColumn);
          }

          const nestedColumns = ((classColumn.columns as ColumnsModel[]) ?? []);
          classColumn.columns = nestedColumns;
          if (!nestedColumns.some((column) => column.field === field)) {
            nestedColumns.push({
              field,
              label: getDeletedLabel(),
              type,
              operators: defaultOperatorsByType[type],
              category: translateQueryBuilder("schemaAttributes"),
            });
          }
        }
      }
      return;
    }

    const attributesPrefix = "assetidentity.attributes.";
    const hierarchyPrefix = "assetidentity.hierarchyattributes.";
    if (field.startsWith(externalAttributeApiPrefix)) {
      const externalAttributeDefinitionId = field.substring(externalAttributeApiPrefix.length);
      if (!schemaInfo.knownExternalAttributeIds.has(externalAttributeDefinitionId) &&
          !deletedStandaloneColumnsByField.has(field)) {
        deletedStandaloneColumnsByField.set(field, {
          field,
          label: getDeletedLabel(),
          type,
          operators: defaultOperatorsByType[type],
          category: getExternalAttributeCategoryFallback(),
        });
      }
    } else if (field.startsWith(attributesPrefix) || field.startsWith(hierarchyPrefix)) {
      const attributePart = field.substring(field.lastIndexOf(".") + 1);
      const individualIds = attributePart.split("|");
      const anyKnown = individualIds.some((id) => schemaInfo.knownAttributeIds.has(id));
      if (!anyKnown && !deletedStandaloneColumnsByField.has(field)) {
        deletedStandaloneColumnsByField.set(field, {
          field,
          label: getDeletedLabel(),
          type,
          operators: defaultOperatorsByType[type],
          category: translateQueryBuilder("schemaAttributes"),
        });
      }
    }
  });

  classColumns.forEach((classColumn) => {
    const nestedColumns = ((classColumn.columns as ColumnsModel[]) ?? []);
    classColumn.columns = sortColumnsByCategoryAndLabelAsc(nestedColumns);
  });

  return [
    {
      field: "assetidentity.name",
      label: translateQueryBuilder("assetName"),
      type: "string",
      operators: getQueryBuilderOperators(["startswith", "notstartswith", "endswith", "notendswith", "contains", "notcontains", "equal", "notequal"]),
      category: translateQueryBuilder("assetProperties"),
    },
    {
      field: "assetidentity.assetclass.name",
      label: translateQueryBuilder("assetClass"),
      type: "string",
      values: assetClassNames,
      operators: getQueryBuilderOperators(["in", "notin"]),
      category: translateQueryBuilder("assetProperties"),
    },
    ...classColumns,
    ...Array.from(deletedStandaloneColumnsByField.values()),
  ];
}

// Remove empty string rules (string type with null/empty value and operator other than isnull/isnotnull)
export const cleanEmptyStringRules = (rules: any): any => {
  if (!rules) return rules;

  const isEmptyStringRule = (rule: any): boolean => {
    if (!rule.field) return true;
    if (rule.type !== "string") return false;
    if (rule.operator === "isnull" || rule.operator === "isnotnull") return false;
    // Check for "in" or "notin" with null or empty array
    if (rule.operator === "in" || rule.operator === "notin") {
      return rule.value === null || rule.value === undefined || (Array.isArray(rule.value) && rule.value.length === 0);
    }
    return rule.value === null || rule.value === undefined || rule.value === "";
  };

  const cleanRule = (rule: any): any => {
    if (rule.rules && Array.isArray(rule.rules)) {
      // This is a group, recursively clean child rules and filter out empty ones
      const cleanedRules = rule.rules.map(cleanRule).filter((r: any) => r !== null);
      return {
        ...rule,
        rules: cleanedRules,
      };
    } else if (isEmptyStringRule(rule)) {
      // Remove this rule
      return null;
    }
    return rule;
  };

  return cleanRule(rules);
};

// Transform query rules for API: convert "assetClassId/attributeId" to the appropriate prefix
// based on whether the attribute is a regular attribute or hierarchy attribute.
export const transformRulesForApi = (rules: any, assetClasses: IAssetClassDetail[] = []): any => {
  if (!rules) return rules;

  const assetClassMaps = buildAssetClassMaps(assetClasses);

  const transformRule = (rule: any): any => {
    if (rule.rules && Array.isArray(rule.rules)) {
      // This is a group, recursively transform child rules
      return {
        ...rule,
        rules: rule.rules.map(transformRule),
      };
    } else if (rule.field) {
      if (rule.field === "assetidentity.assetclass.name") {
        return {
          ...rule,
          field: "assetidentity.assetclass.id",
          value: mapAssetClassValueToIds(rule.value, assetClassMaps),
        };
      }

      // This is a rule with a field
      const fieldParts = rule.field.split("/");
      if (fieldParts.length === 2) {
        const fullPath = rule.field;
        
        // Check if this is a hierarchy attribute
        if (hierarchyAttributePaths.has(fullPath)) {
          return {
            ...rule,
            field: "assetidentity.hierarchyattributes." + fieldParts[1],
          };
        } else if (hasExternalAttributeDefinition(assetClasses, fieldParts[0], fieldParts[1])) {
          const definition = getExternalAttributeDefinition(assetClasses, fieldParts[0], fieldParts[1]);
          return {
            ...rule,
            field: externalAttributeApiPrefix + fieldParts[1],
            value: definition?.DataType === AttributeDataType.Date
              ? normalizeExternalDateValueForApi(rule.value, parseLocalizedQueryBuilderDate)
              : rule.value,
          };
        } else {
          // Transform to "assetidentity.attributes.attributeId"
          return {
            ...rule,
            field: "assetidentity.attributes." + fieldParts[1],
          };
        }
      }
    }
    return rule;
  };

  return transformRule(rules);
};

// Check if rules are empty (no valid rules after cleaning)
export const hasValidRules = (rules: any): boolean => {
  if (!rules) return false;
  if (!rules.rules || !Array.isArray(rules.rules)) return false;
  return hasLeafRules(rules as QueryRuleNode);
};

export const hasDegenerateRules = (
  rules: any,
  assetClasses: IAssetClassDetail[],
  deletedAssetClassLabels: string[] = [],
  dataSources: DataSourceModel[] = []
): boolean => {
  if (!rules) return false;
  const schemaInfo = getSchemaInfo(assetClasses ?? [], dataSources);
  const knownAssetClassNames = new Set(assetClasses.map((assetClass) => assetClass.Name).filter(Boolean));
  const deletedAssetClassLabelSet = new Set(deletedAssetClassLabels);

  let degenerateFound = false;

  walkRuleFields(rules as QueryRuleNode, (rule) => {
    if (degenerateFound || typeof rule.field !== "string") {
      return;
    }

    const field = rule.field;

    if (field === "assetidentity.assetclass.name") {
      const hasDeletedValue = Array.isArray(rule.value)
        ? rule.value.some((value) =>
            typeof value === "string" && (!!deletedAssetClassLabelSet.has(value) || !knownAssetClassNames.has(value))
          )
        : typeof rule.value === "string" && (!!deletedAssetClassLabelSet.has(rule.value) || !knownAssetClassNames.has(rule.value));

      if (hasDeletedValue) {
        degenerateFound = true;
      }
      return;
    }

    const slashFieldParts = field.split("/");
    if (slashFieldParts.length === 2) {
      if (!schemaInfo.validClassAttributePaths.has(field)) {
        const attributePart = slashFieldParts[1];
        if (schemaInfo.knownExternalAttributeIds.has(attributePart)) {
          degenerateFound = true;
          return;
        }

        const individualIds = attributePart.split("|");
        const allValid = individualIds.length > 1 && individualIds.every((id) => schemaInfo.knownAttributeIds.has(id));
        if (!allValid) {
          degenerateFound = true;
        }
      }
      return;
    }

    const attributesPrefix = "assetidentity.attributes.";
    const hierarchyPrefix = "assetidentity.hierarchyattributes.";
    if (field.startsWith(externalAttributeApiPrefix)) {
      const externalAttributeDefinitionId = field.substring(externalAttributeApiPrefix.length);
      if (!schemaInfo.knownExternalAttributeIds.has(externalAttributeDefinitionId) ||
          !schemaInfo.validExternalAttributeIds.has(externalAttributeDefinitionId)) {
        degenerateFound = true;
      }
    } else if (field.startsWith(attributesPrefix) || field.startsWith(hierarchyPrefix)) {
      const attributePart = field.substring(field.lastIndexOf(".") + 1);
      const individualIds = attributePart.split("|");
      const anyKnown = individualIds.some((id) => schemaInfo.knownAttributeIds.has(id));
      if (!anyKnown) {
        degenerateFound = true;
      }
    }
  });

  return degenerateFound;
};

export type QueryBuilderHandle = {
  getRules: () => RuleModel | null;
  getCleanedRules: () => RuleModel | null;
  getApiRules: () => RuleModel | null;
  hasValidRules: () => boolean;
  hasDegenerateRules: () => boolean;
  getSqlFromRules: (rules: RuleModel | undefined) => string;
};

type AssetQueryBuilderProps = {
  assetClasses?: IAssetClassDetail[];
  initialRules?: unknown;
  onRulesChange: (rules: unknown) => void;
};

type DataSourceLoadState = {
  facilityId?: string;
  loaded: boolean;
  sources: DataSourceModel[];
};

const getExternalSchemaClasses = (): AssetClassExternalSchema[] =>
  (FacilityStore.getAssetClasses() ?? []) as unknown as AssetClassExternalSchema[];

const AssetQueryBuilder = forwardRef<QueryBuilderHandle, AssetQueryBuilderProps>(
  ({ assetClasses, initialRules, onRulesChange }, ref) => {
    const [externalSchemaClasses, setExternalSchemaClasses] = useState<AssetClassExternalSchema[]>(
      getExternalSchemaClasses
    );
    const selectedFacilityId = FacilityStore.getSelectedFacility()?.ID;
    const [dataSourceLoadState, setDataSourceLoadState] = useState<DataSourceLoadState>({
      facilityId: selectedFacilityId,
      loaded: !selectedFacilityId,
      sources: [],
    });

    useEffect(() => {
      const handleFacilityStoreChange = () => {
        setExternalSchemaClasses(getExternalSchemaClasses());
      };

      FacilityStore.addChangeListener(handleFacilityStoreChange);
      handleFacilityStoreChange();

      return () => {
        FacilityStore.removeChangeListener(handleFacilityStoreChange);
      };
    }, []);

    const safeAssetClasses = useMemo(
      () => mergeExternalSchema(assetClasses ?? [], externalSchemaClasses),
      [assetClasses, externalSchemaClasses]
    );

    useEffect(() => {
      if (!selectedFacilityId) {
        setDataSourceLoadState({ loaded: true, sources: [] });
        return;
      }

      let cancelled = false;
      setDataSourceLoadState({ facilityId: selectedFacilityId, loaded: false, sources: [] });
      dataSourcesApi.getSources(selectedFacilityId)
        .then((sources) => {
          if (!cancelled) {
            setDataSourceLoadState({ facilityId: selectedFacilityId, loaded: true, sources: sources ?? [] });
          }
        })
        .catch(() => {
          if (!cancelled) {
            setDataSourceLoadState({ facilityId: selectedFacilityId, loaded: true, sources: [] });
          }
        });

      return () => {
        cancelled = true;
      };
    }, [selectedFacilityId]);

    const dataSourcesLoaded = dataSourceLoadState.loaded && dataSourceLoadState.facilityId === selectedFacilityId;
    const dataSources = dataSourcesLoaded ? dataSourceLoadState.sources : [];

    const resolvedInitialRules = useMemo(
      () => {
        const queryBuilderRules = convertApiRulesToQueryBuilderRules(initialRules as QueryRuleNode | undefined, safeAssetClasses) as QueryRuleNode | undefined;
        const normalizedInitialRules = normalizeAssetClassRulesForUi(queryBuilderRules, safeAssetClasses);
        const deleted = resolveDeletedAssetClassValues(normalizedInitialRules, safeAssetClasses);
        const consolidated = resolveConsolidatedRules(deleted.resolvedRules as QueryRuleNode | undefined, safeAssetClasses);
        const normalizedTree = collapseRedundantRuleGroups(consolidated);
        return {
          resolvedRules: normalizedTree,
          deletedAssetClassLabels: deleted.deletedAssetClassLabels,
        };
      },
      [initialRules, safeAssetClasses]
    );
    const columns = useMemo(
      () => (safeAssetClasses.length && dataSourcesLoaded
        ? buildColumns(safeAssetClasses, resolvedInitialRules.resolvedRules, resolvedInitialRules.deletedAssetClassLabels, dataSources)
        : fallbackColumns),
      [safeAssetClasses, resolvedInitialRules, dataSourcesLoaded, dataSources]
    );
    const queryBuilderRef = useRef<QueryBuilderComponentType | null>(null);
    const assetNameButtonRef = useRef<ButtonComponent | null>(null);

    const syncAddButtonTitle = useCallback(() => {
      const queryBuilderElement = (queryBuilderRef.current as any)?.element as HTMLElement | undefined;
      const addButtons = queryBuilderElement?.querySelectorAll('.e-add-btn') as NodeListOf<HTMLElement> | undefined;
      if (!addButtons?.length) {
        return;
      }

      addButtons.forEach((addButton) => {
        addButton.setAttribute('title', translateQueryBuilder('addGroupOrCondition'));
      });
    }, []);

    // Build sorted asset class items for the split button dropdown
    const assetClassItems: ItemModel[] = useMemo(() => {
      if (!safeAssetClasses || safeAssetClasses.length === 0) return [];
      return [...safeAssetClasses]
        .filter((ac) => ac.Name)
        .sort((a, b) => (a.Name || "").localeCompare(b.Name || ""))
        .map((ac) => ({
          text: ac.Name,
          id: ac.Name,
        }));
    }, [safeAssetClasses]);

    // Add a rule to the query builder (appending to existing rules)
    const addRuleToQueryBuilder = useCallback((field: string, operator: string, value: string | string[] | null = null) => {
      const queryBuilder = queryBuilderRef.current;
      if (!queryBuilder) return;

      const currentRules = queryBuilder.getRules();
      const newRule: RuleModel = {
        field,
        label: field === "assetidentity.name" ? translateQueryBuilder("assetName") : translateQueryBuilder("assetClass"),
        operator,
        value: value === null ? undefined : value,
        type: "string",
      };

      // Build new rules array by appending
      const existingRules = getRenderableRootRules(currentRules);
      const updatedRules: RuleModel = {
        condition: currentRules?.condition || "and",
        rules: [...existingRules, newRule],
      };

      // Set the updated rules
      queryBuilder.setRules(updatedRules);

      // Trigger change event
      requestAnimationFrame(() => {
        queryBuilder.refresh();
        syncAddButtonTitle();

        const rules = queryBuilder.getRules();
        onRulesChange(rules);
      });
    }, [onRulesChange, syncAddButtonTitle]);

    // Quick search: Add "Asset Name" StartsWith condition
    const handleAddNameCondition = useCallback(() => {
      addRuleToQueryBuilder("assetidentity.name", "startswith");
    }, [addRuleToQueryBuilder]);

    useEffect(() => {
      const buttonElement = assetNameButtonRef.current?.element;
      if (!buttonElement) {
        return;
      }

      buttonElement.addEventListener("click", handleAddNameCondition);
      return () => buttonElement.removeEventListener("click", handleAddNameCondition);
    }, [handleAddNameCondition]);

    // Quick search: Add "Asset Class" In condition (generic, no value)
    const handleAddAssetClassCondition = useCallback(() => {
      addRuleToQueryBuilder("assetidentity.assetclass.name", "in");
    }, [addRuleToQueryBuilder]);

    // Quick search: Add "Asset Class" In condition with specific class selected
    const handleAssetClassItemSelect = useCallback((args: MenuEventArgs) => {
      if (args.item?.id) {
        addRuleToQueryBuilder("assetidentity.assetclass.name", "in", [args.item.id]);
      }
    }, [addRuleToQueryBuilder]);

    useImperativeHandle(ref, () => ({
      getRules: () => queryBuilderRef.current?.getRules() ?? null,
      getCleanedRules: () => {
        const rules = queryBuilderRef.current?.getRules();
        return rules ? cleanEmptyStringRules(rules) : null;
      },
      getApiRules: () => {
        const rules = queryBuilderRef.current?.getRules();
        if (!rules) return null;
        const cleaned = cleanEmptyStringRules(rules);
        return cleaned ? transformRulesForApi(cleaned, safeAssetClasses) : null;
      },
      hasValidRules: () => {
        const rules = queryBuilderRef.current?.getRules();
        if (!rules) return false;
        const cleaned = cleanEmptyStringRules(rules);
        if (!hasValidRules(cleaned)) {
          return false;
        }
        return !hasDegenerateRules(cleaned, safeAssetClasses, resolvedInitialRules.deletedAssetClassLabels, dataSources);
      },
      hasDegenerateRules: () => {
        const rules = queryBuilderRef.current?.getRules();
        if (!rules) return false;
        const cleaned = cleanEmptyStringRules(rules);
        return hasDegenerateRules(cleaned, safeAssetClasses, resolvedInitialRules.deletedAssetClassLabels, dataSources);
      },
      getSqlFromRules: (rules) => queryBuilderRef.current?.getSqlFromRules(rules) ?? "",
    }), [safeAssetClasses, resolvedInitialRules, dataSources]);

    const handleRuleChange = useCallback(() => {
      const queryBuilder = queryBuilderRef.current;
      if (!queryBuilder) {
        return;
      }

      const rules = queryBuilder.getRules();
      syncAddButtonTitle();
      onRulesChange(rules);
    }, [onRulesChange, syncAddButtonTitle]);

    useEffect(() => {
      const queryBuilder = queryBuilderRef.current;
      if (!queryBuilder || columns.length === 0) {
        return;
      }

      const actualInitialRules = resolvedInitialRules.resolvedRules as RuleModel | undefined;
      const hasInitialRules = hasLeafRules(actualInitialRules as QueryRuleNode | undefined);

      // Initialize from initialRules prop if provided
      if (hasInitialRules && actualInitialRules) {
        try {
          queryBuilder.setRules(actualInitialRules);
        } catch (error) {
          console.warn("Failed to restore QueryBuilder rules.", error);
        }
      }

      let refreshFrame = 0;
      const frame = requestAnimationFrame(() => {
        refreshFrame = requestAnimationFrame(() => {
          if (hasInitialRules) {
            queryBuilder.refresh();
          } else {
            clearRenderedRules(queryBuilder);
          }

          syncAddButtonTitle();

          const rules = queryBuilder.getRules();
          if (hasInitialRules && rules) {
            onRulesChange(rules);
          }
        });
      });

      return () => {
        cancelAnimationFrame(frame);
        if (refreshFrame) {
          cancelAnimationFrame(refreshFrame);
        }
      };
    }, [columns, resolvedInitialRules, onRulesChange, syncAddButtonTitle]);

    return (
      <>
        <div className="query-builder-quick-search">
          <span className="quick-search-label">{translateQueryBuilder("quickAdd")}</span>
          <ButtonComponent
            ref={assetNameButtonRef}
            cssClass="e-small e-outline"
            iconCss="e-icons e-rename name-condition-icon"
          >
            {translateQueryBuilder("assetName")}
          </ButtonComponent>
          <SplitButtonComponent
            cssClass="e-small e-outline"
            iconCss="e-icons e-list-unordered asset-class-condition-icon"
            items={assetClassItems}
            select={handleAssetClassItemSelect}
            click={handleAddAssetClassCondition}
          >
            {translateQueryBuilder("assetClass")}
          </SplitButtonComponent>
        </div>
        {dataSourcesLoaded && (
          <QueryBuilderComponent
            style={{borderTop: 0}}
            ref={queryBuilderRef}
            dataSource={dataSource}
            columns={columns}
            locale={InovxLoader.getSyncfusionJS2Locale() || undefined}
            fieldMode="Default"
            valueModel={{
              numericTextBoxModel: {
                showSpinButton: false,
                min: -Number.MAX_VALUE,
              },
            }}
            ruleChange={handleRuleChange}
            separator="/"
            allowDragAndDrop={true}
          />
        )}
      </>
    );
  }
);

AssetQueryBuilder.displayName = "AssetQueryBuilder";

export default AssetQueryBuilder;
